import React, { useRef, useEffect, useCallback } from "react";
import { IVideoPlayerProps } from "../../models/interfaces";
import { useHlsPlayback } from "../useHlsPlayback";
import "./style.css";

function isHTMLVideoElement(el: HTMLMediaElement | null): el is HTMLVideoElement {
	return el instanceof HTMLVideoElement;
}

const VideoPlayer: React.FC<IVideoPlayerProps> = ({
	mediaUrl,
	posterUrl,
	ariaLabel,
	onTimeUpdate,
	onPlayStateChange,
	onError,
	onReady,
	seekToTime,
	seekTrigger,
	onSeeked,
	pauseTrigger,
	playTrigger,
	presignedSegmentUrls,
}) => {
	const { mediaRef, isLoading, hasError, handleLoadedMetadataOrData, handleNativeError } = useHlsPlayback({
		mediaUrl,
		presignedSegmentUrls,
		onReady,
		onError,
	});

	const videoRef = mediaRef as React.RefObject<HTMLMediaElement>;
	const isSeekingRef = useRef(false);
	const prevSeekTriggerRef = useRef<number>(seekTrigger ?? 0);
	const prevPauseTriggerRef = useRef<number>(pauseTrigger ?? 0);
	const prevPlayTriggerRef = useRef<number>(playTrigger ?? 0);

	useEffect(() => {
		if (seekTrigger === prevSeekTriggerRef.current) return;
		prevSeekTriggerRef.current = seekTrigger ?? 0;
		if (seekToTime == null) return;
		const video = videoRef.current;
		if (!isHTMLVideoElement(video)) return;
		video.currentTime = seekToTime;
	}, [seekToTime, seekTrigger]);

	useEffect(() => {
		if (pauseTrigger === prevPauseTriggerRef.current) return;
		prevPauseTriggerRef.current = pauseTrigger ?? 0;
		const video = videoRef.current;
		if (!isHTMLVideoElement(video) || video.paused) return;
		video.pause();
	}, [pauseTrigger]);

	useEffect(() => {
		if (playTrigger === prevPlayTriggerRef.current) return;
		prevPlayTriggerRef.current = playTrigger ?? 0;
		const video = videoRef.current;
		if (!isHTMLVideoElement(video) || !video.paused) return;
		video.play().catch((err: DOMException) => {
			if (err.name === "AbortError") return;
			onError?.(err);
			onPlayStateChange?.(false);
		});
	}, [playTrigger, onError, onPlayStateChange]);

	const handleTimeUpdate = useCallback(() => {
		const video = videoRef.current;
		if (!isHTMLVideoElement(video)) return;
		onTimeUpdate?.(video.currentTime);
	}, [onTimeUpdate]);

	const handlePlay = useCallback(() => {
		onPlayStateChange?.(true);
	}, [onPlayStateChange]);

	const handlePause = useCallback(() => {
		if (!isSeekingRef.current) onPlayStateChange?.(false);
	}, [onPlayStateChange]);

	const handleSeeked = useCallback(() => {
		isSeekingRef.current = false;
		const video = videoRef.current;
		if (!isHTMLVideoElement(video)) return;
		const time = video.currentTime;
		onTimeUpdate?.(time);
		onSeeked?.(time);
	}, [onTimeUpdate, onSeeked]);

	const handleSeeking = useCallback(() => {
		isSeekingRef.current = true;
	}, []);

	const handlePlaying = useCallback(() => {
		isSeekingRef.current = false;
	}, []);

	if (!mediaUrl) {
		return (
			<div className="vp-error-container" role="alert">
				<i className="material-symbols-outlined text-4xl text-[--neutral-grey]" aria-hidden="true">
					error
				</i>
				<p className="mt-2 text-[--neutral-grey] text-base-1">Video unavailable</p>
			</div>
		);
	}

	return (
		<div className="vp-container">
			{isLoading && !hasError && (
				<div className="vp-loading-overlay" role="status" aria-label="Loading video">
					<div className="vp-buffering-spinner" />
				</div>
			)}

			{hasError && (
				<div className="vp-error-overlay" role="alert">
					<i className="material-symbols-outlined text-4xl text-[--neutral-grey]" aria-hidden="true">
						error
					</i>
					<p className="mt-2 text-[--neutral-grey] text-base-1">Failed to load video</p>
				</div>
			)}

			<video
				ref={videoRef}
				controls
				poster={posterUrl || undefined}
				controlsList="nodownload noremoteplayback"
				disablePictureInPicture
				disableRemotePlayback
				aria-label={ariaLabel || "Video player"}
				// Cosmetic only: prevents casual right-click save. Not a security boundary — src URL remains accessible via DevTools.
				onContextMenu={(e) => e.preventDefault()}
				onTimeUpdate={handleTimeUpdate}
				onSeeking={handleSeeking}
				onSeeked={handleSeeked}
				onPlaying={handlePlaying}
				onLoadedData={handleLoadedMetadataOrData}
				onError={handleNativeError}
				onPlay={handlePlay}
				onPause={handlePause}
				className={`vp-video-element ${hasError ? "vp-hidden" : ""}`}
			/>
		</div>
	);
};

export default React.memo(VideoPlayer);
